/**
 * Expanded curve editor: geometry, drawing and hit-testing (W2-04b curve-editor; DESIGN.md §4.7, §5.6.2, §6.2, §6.4;
 * ARCHITECTURE.md §7.7).
 *
 * The curve editor shows ONE property of one line (one layer, or every layer side by side in the Channel view) as a
 * graph that stays aligned with the note lane: time runs up the shared timeline axis, the VALUE runs horizontally
 * (DESIGN.md §4.7 "same convention everywhere"). This module is pure render code: it receives a prepared scene
 * (keys, sampled curves, labels already translated) from the feature layer and draws it on the host's three
 * canvases, answers hit-tests and marquee queries, and converts pointer positions back into handle parameters.
 *
 * Geometry:
 *  - a lane spans [x0, x0 + width] of its column; its `ValueAxis` maps values to x with a small padding;
 *  - a segment runs from key i (beat b_i, leave value vOut_i) to key i+1 (beat b_{i+1}, arrive value vIn_{i+1});
 *    its progress is measured in SECONDS (Phira computes it that way, so a segment spanning a BPM change eases in
 *    real time): progress p maps to the beat `beatAtSec(s_i + p (s_{i+1} - s_i))`;
 *  - Bezier handles (DESIGN.md §4.7): control point 1 sits at (value v0 + y1 (v1 - v0), progress x1), control point 2
 *    at (v0 + y2 (v1 - v0), progress x2); x1 / x2 are clamped to [0, 1] when dragged;
 *  - slice handles: a narrow rail beside the segment shows the WHOLE easing curve f(u), u in [0, 1] (Phira's tween
 *    in the same progress direction as the segment), with the used part [left, right] highlighted and a handle at
 *    each end.
 *
 * Every colour comes from the design tokens (`readCurveTheme`: curve tokens first, then the timeline theme's).
 */

import { Glyphs, hatchRect, KEY_SIZE } from '../glyphs';
import { canvasFont, type TimelineTheme } from '../theme';
import type { SampledCurve } from '../curveCache';
import { axisContext } from '../axis';
import { drawBeatGrid } from '../gutterColumn';
import type { TimeAxis, TimelineHit, TimelineRectItems } from '../../types';
import type { Ease } from '../../../core/ease/types';
import type { KeyId } from '../../../core/model/types';

// ---------------------------------------------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------------------------------------------

/** Horizontal padding inside a lane so keys at the ends of the value range stay grabbable. */
export const LANE_PAD_X = 14;
/** Height of the value-label band at the top of a lane. */
export const VALUE_BAND_PX = 16;
/** Pick tolerance for keys (CSS px). */
export const KEY_PICK_PX = 7;
/** Pick tolerance for the curve of a segment. */
export const CURVE_PICK_PX = 6;
/** Radius of a Bezier handle (drawn and picked). */
export const BEZIER_HANDLE_R = 4;
/** Slice rail: width of the mini curve, gap to the segment, handle size. */
export const SLICE_RAIL_W = 18;
export const SLICE_RAIL_GAP = 10;
export const SLICE_HANDLE_W = 12;
export const SLICE_HANDLE_H = 6;
/** Segments shorter than this on screen get no handles or labels. */
export const MIN_HANDLE_SEGMENT_PX = 16;
export const MIN_LABEL_SEGMENT_PX = 28;
/** Opacity of other layers' ghost curves (DESIGN.md §4.7: "other layers' ghosts at 20 %"). */
export const GHOST_ALPHA = 0.2;
/** Samples of the whole easing curve drawn on a slice rail. */
const RAIL_SAMPLES = 32;

// ---------------------------------------------------------------------------------------------------------------
// Theme
// ---------------------------------------------------------------------------------------------------------------

/** Curve colours on top of the timeline theme. */
export interface CurveTheme extends TimelineTheme {
  readonly curve: string;
  readonly curveGuide: string;
  readonly overshoot: string;
  readonly baked: string;
  readonly danger: string;
  readonly surface: string;
}

/** Token candidates, most specific first; the last entry is a CSS system colour (never a literal colour). */
const CURVE_TOKENS: Readonly<Record<Exclude<keyof CurveTheme, keyof TimelineTheme>, readonly string[]>> = {
  curve: ['--color-curve', '--color-accent', 'Highlight'],
  curveGuide: ['--color-curve-guide', '--color-grid-beat', 'GrayText'],
  overshoot: ['--color-curve-overshoot', '--color-warning-muted', 'Mark'],
  baked: ['--color-badge-baked', '--color-warning', 'Mark'],
  danger: ['--color-danger', '--color-playhead', 'Mark'],
  surface: ['--color-surface-2', '--color-surface-1', 'Canvas'],
};

/** Builds the curve theme from a token reader on top of a timeline theme. */
export function curveThemeFromTokens(base: TimelineTheme, read: (name: string) => string): CurveTheme {
  const out: Record<string, string> = {};
  for (const [key, candidates] of Object.entries(CURVE_TOKENS)) {
    let value = '';
    for (const c of candidates) {
      if (!c.startsWith('--')) {
        value = c;
        break;
      }
      const v = read(c).trim();
      if (v !== '') {
        value = v;
        break;
      }
    }
    out[key] = value;
  }
  return Object.freeze({ ...base, ...(out as Record<Exclude<keyof CurveTheme, keyof TimelineTheme>, string>) });
}

const themeCache = new WeakMap<TimelineTheme, CurveTheme>();

/**
 * The curve theme for the canvas being drawn: reads the curve tokens from the element's computed style once per
 * timeline theme object (the host replaces that object on a theme switch).
 */
export function readCurveTheme(base: TimelineTheme, el: Element | null): CurveTheme {
  const hit = themeCache.get(base);
  if (hit !== undefined) return hit;
  let style: CSSStyleDeclaration | null = null;
  if (el !== null && typeof getComputedStyle === 'function') {
    try {
      style = getComputedStyle(el);
    } catch {
      style = null;
    }
  }
  const theme = curveThemeFromTokens(base, (name) => style?.getPropertyValue(name) ?? '');
  themeCache.set(base, theme);
  return theme;
}

// ---------------------------------------------------------------------------------------------------------------
// Value axis
// ---------------------------------------------------------------------------------------------------------------

export interface ValueRange {
  readonly lo: number;
  readonly hi: number;
}

/** Maps values to x inside a lane (left = lo, right = hi). */
export interface ValueAxis extends ValueRange {
  /** Pixel span of the value range (column px). */
  readonly x0: number;
  readonly x1: number;
  /** Value units per CSS px (> 0). */
  readonly perPx: number;
  readonly valueToX: (v: number) => number;
  readonly xToValue: (x: number) => number;
}

export function createValueAxis(range: ValueRange, x0: number, x1: number): ValueAxis {
  const lo = Number.isFinite(range.lo) ? range.lo : 0;
  let hi = Number.isFinite(range.hi) ? range.hi : 1;
  if (!(hi > lo)) hi = lo + 1;
  const left = x0;
  const right = x1 > x0 ? x1 : x0 + 1;
  const scale = (right - left) / (hi - lo);
  return {
    lo,
    hi,
    x0: left,
    x1: right,
    perPx: (hi - lo) / (right - left),
    valueToX: (v) => left + (v - lo) * scale,
    xToValue: (x) => lo + (x - left) / scale,
  };
}

/** The value axis of a lane (its padded inner span). */
export function laneValueAxis(range: ValueRange, laneX0: number, laneWidth: number): ValueAxis {
  const pad = Math.min(LANE_PAD_X, laneWidth / 4);
  return createValueAxis(range, laneX0 + pad, laneX0 + laneWidth - pad);
}

/** 1, 2 or 5 times a power of ten, the smallest such step >= raw (raw > 0). */
export function niceStep(raw: number): number {
  if (!(raw > 0) || !Number.isFinite(raw)) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(raw)));
  const m = raw / p;
  const k = m <= 1 + 1e-9 ? 1 : m <= 2 + 1e-9 ? 2 : m <= 5 + 1e-9 ? 5 : 10;
  return k * p;
}

/** Rounds to a multiple of `step`, removing binary noise (0.1 + 0.2 -> 0.3). */
export function roundToStep(v: number, step: number): number {
  if (!(step > 0) || !Number.isFinite(v)) return v;
  const q = Math.round(v / step) * step;
  const decimals = Math.max(0, Math.min(12, -Math.floor(Math.log10(step)) + 1));
  return Number(q.toFixed(decimals));
}

/** Tick values of a 1-2-5 grid covering [lo, hi] with about `count` ticks. */
export function niceTicks(lo: number, hi: number, count: number): { readonly step: number; readonly ticks: number[] } {
  if (!(hi > lo) || !Number.isFinite(lo) || !Number.isFinite(hi)) return { step: 1, ticks: [] };
  const step = niceStep((hi - lo) / Math.max(1, count));
  const ticks: number[] = [];
  const first = Math.ceil(lo / step - 1e-9);
  const last = Math.floor(hi / step + 1e-9);
  for (let k = first; k <= last && ticks.length < 200; k++) ticks.push(roundToStep(k * step, step));
  return { step, ticks };
}

export interface FitOptions {
  /** A range the fit always includes (opacity 0..255, speed from 0). */
  readonly natural?: ValueRange | null;
  /** Fraction of the span added on each side. Default 0.08. */
  readonly pad?: number;
  /** Smallest span (value units) for a flat curve. Default 1. */
  readonly minSpan?: number;
}

/**
 * Auto-fit value range of a lane: min/max of the given finite values (and the natural range), padded, never empty.
 * Returns null when nothing is finite and there is no natural range.
 */
export function fitValueRange(values: Iterable<number>, opts: FitOptions = {}): ValueRange | null {
  let lo = Number.POSITIVE_INFINITY;
  let hi = Number.NEGATIVE_INFINITY;
  for (const v of values) {
    if (!Number.isFinite(v)) continue;
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  const natural = opts.natural ?? null;
  if (natural !== null) {
    lo = Math.min(lo, natural.lo);
    hi = Math.max(hi, natural.hi);
  }
  if (!(lo <= hi)) return null;
  const minSpan = opts.minSpan ?? 1;
  if (hi - lo < minSpan) {
    const mid = (lo + hi) / 2;
    const half = Math.max(minSpan, Math.abs(mid) * 0.1) / 2;
    lo = mid - half;
    hi = mid + half;
  }
  const pad = (hi - lo) * (opts.pad ?? 0.08);
  return { lo: lo - pad, hi: hi + pad };
}

/** Zooms a value range by `factor` (> 1 zooms in) keeping `anchor` in place. */
export function zoomValueRange(r: ValueRange, factor: number, anchor: number): ValueRange {
  const f = Number.isFinite(factor) && factor > 0 ? factor : 1;
  const lo = anchor - (anchor - r.lo) / f;
  const hi = anchor + (r.hi - anchor) / f;
  if (!(hi - lo > 1e-9)) return r;
  return { lo, hi };
}

export function panValueRange(r: ValueRange, dv: number): ValueRange {
  return Number.isFinite(dv) ? { lo: r.lo + dv, hi: r.hi + dv } : r;
}

// ---------------------------------------------------------------------------------------------------------------
// Scene
// ---------------------------------------------------------------------------------------------------------------

/** Beat <-> chart seconds (the engine's tempo map: `secAtBeatF64` / `beatF64At`). */
export interface CurveTimeMap {
  secAtBeat(beat: number): number;
  beatAtSec(sec: number): number;
}

export interface CurveKeyView {
  readonly id: KeyId;
  /** f64 beat for drawing. */
  readonly beat: number;
  /** Chart seconds of the key (Phira-exact). */
  readonly sec: number;
  readonly vIn: number;
  readonly vOut: number;
  /** Ease of the segment that starts here (unused on the last key). */
  readonly ease: Ease;
  readonly link: number;
  readonly selected: boolean;
  /** The segment starting at this key is selected. */
  readonly segmentSelected: boolean;
}

/** The speed view's extra curves (floorOverlay.ts draws them). */
export interface SpeedLaneOverlay {
  /** dH/dt of the floor curve the active integrator produces, RPE speed units. */
  readonly effective: SampledCurve | null;
  /** Floor position (distance travelled) in RPE-y px. */
  readonly distance: SampledCurve | null;
  /** Value range of the distance axis (RPE-y px). */
  readonly distanceRange: ValueRange | null;
}

export interface CurveLaneView {
  /** Lane id: the layer index as a string, or 'sum' for the read-only Σ lane. */
  readonly id: string;
  readonly x0: number;
  readonly width: number;
  readonly axis: ValueAxis;
  readonly keys: readonly CurveKeyView[];
  /** The lane's own channel curve. */
  readonly curve: SampledCurve | null;
  /**
   * 'main': the curve is what plays (accent colour, solid). 'guide': the keyframe curve of the speed view, drawn
   * thin and dashed under the effective speed (floorOverlay.ts), which is what actually moves the notes.
   */
  readonly curveStyle: 'main' | 'guide';
  /** Other layers' curves, drawn faintly. */
  readonly ghosts: readonly SampledCurve[];
  /** Effective Σ over layers (dashed), or null. */
  readonly sigma: SampledCurve | null;
  /** Keys can be dragged / added (false for the Σ lane and while a line is locked). */
  readonly editable: boolean;
  /** Bezier handles may be shown and dragged (never on speed or effect / video variables). */
  readonly bezierHandles: boolean;
  /** Slice handles may be shown (tween segments: sliced ones always, selected ones on demand). */
  readonly sliceHandles: boolean;
  /** Values below this are hatched (opacity < 0 hides the line's notes), or null. */
  readonly hatchBelow: number | null;
  /** Easing badge per segment (index of the start key), translated; null = none. */
  readonly segmentLabels: readonly (string | null)[];
  /** Label per key ("12:1/4 = 135"), translated; null = none. */
  readonly keyLabels: readonly (string | null)[];
  /**
   * Friendly display unit = raw value · displayScale (opacity 1/2.55, Length 50, sizes 100, others 1): the value grid
   * puts its ticks on round DISPLAY values (0 %, 25 %, ...), then maps them back to raw values.
   */
  readonly displayScale: number;
  /** Value tick formatter: RAW value and the tick step (raw units) -> label in friendly units. */
  readonly formatValue: (v: number, step: number) => string;
  /** Text shown when the lane has nothing to draw (absent track), or null. */
  readonly emptyText: string | null;
  readonly speed: SpeedLaneOverlay | null;
  /** Lane highlighted as the focused column. */
  readonly focused: boolean;
  /** Name drawn at the top of the lane when lanes show different properties (Position X | Y), or null. */
  readonly caption?: string | null;
}

export interface CurveScene {
  readonly lanes: readonly CurveLaneView[];
  readonly time: CurveTimeMap;
}

// ---------------------------------------------------------------------------------------------------------------
// Segment geometry
// ---------------------------------------------------------------------------------------------------------------

/** Beat at segment progress p (seconds-based). */
export function beatAtProgress(time: CurveTimeMap, k0: CurveKeyView, k1: CurveKeyView, p: number): number {
  if (p <= 0) return k0.beat;
  if (p >= 1) return k1.beat;
  return time.beatAtSec(k0.sec + p * (k1.sec - k0.sec));
}

/** Segment progress (seconds-based, unclamped) at a beat. */
export function progressAtBeat(time: CurveTimeMap, k0: CurveKeyView, k1: CurveKeyView, beat: number): number {
  const span = k1.sec - k0.sec;
  if (!(span > 0)) return 0;
  return (time.secAtBeat(beat) - k0.sec) / span;
}

export interface Point {
  readonly x: number;
  readonly y: number;
}

/** Screen positions of the two Bezier control points of segment i, or null when it is not a Bezier segment. */
export function bezierHandlePoints(
  lane: CurveLaneView,
  i: number,
  axis: Pick<TimeAxis, 'yAtBeat'>,
  time: CurveTimeMap,
): { readonly p0: Point; readonly h1: Point; readonly h2: Point; readonly p3: Point } | null {
  const k0 = lane.keys[i];
  const k1 = lane.keys[i + 1];
  if (k0 === undefined || k1 === undefined || k0.ease.kind !== 'bezier') return null;
  const [x1, y1, x2, y2] = k0.ease.p;
  const v0 = k0.vOut;
  const v1 = k1.vIn;
  const va = lane.axis;
  return {
    p0: { x: va.valueToX(v0), y: axis.yAtBeat(k0.beat) },
    h1: { x: va.valueToX(v0 + y1 * (v1 - v0)), y: axis.yAtBeat(beatAtProgress(time, k0, k1, x1)) },
    h2: { x: va.valueToX(v0 + y2 * (v1 - v0)), y: axis.yAtBeat(beatAtProgress(time, k0, k1, x2)) },
    p3: { x: va.valueToX(v1), y: axis.yAtBeat(k1.beat) },
  };
}

/**
 * Inverse of a Bezier handle position: the control point (x in progress, y in normalised value) under a pointer.
 * x is clamped to [0, 1] (DESIGN.md §4.7); y is free (overshoot allowed). Null for a flat segment (v0 = v1), where
 * the vertical handle position has no meaning.
 */
export function bezierPointAt(
  lane: CurveLaneView,
  i: number,
  px: number,
  py: number,
  axis: Pick<TimeAxis, 'beatAtY'>,
  time: CurveTimeMap,
): readonly [x: number, y: number] | null {
  const k0 = lane.keys[i];
  const k1 = lane.keys[i + 1];
  if (k0 === undefined || k1 === undefined) return null;
  const v0 = k0.vOut;
  const v1 = k1.vIn;
  if (v1 === v0) return null;
  const x = Math.min(1, Math.max(0, progressAtBeat(time, k0, k1, axis.beatAtY(py))));
  const y = (lane.axis.xToValue(px) - v0) / (v1 - v0);
  return [x, y];
}

/** A tween whose slice handles may be shown: numbered curves other than linear (linear has no slice). */
export function sliceable(e: Ease): e is Extract<Ease, { kind: 'tween' }> {
  return e.kind === 'tween' && e.id >= 2 && e.id <= 29;
}

export function isSliced(e: Ease): boolean {
  return e.kind === 'tween' && (e.left !== 0 || e.right !== 1);
}

/** Whether segment i of a lane shows its slice rail. */
export function showsSliceRail(lane: CurveLaneView, i: number): boolean {
  const k0 = lane.keys[i];
  if (!lane.sliceHandles || k0 === undefined || lane.keys[i + 1] === undefined) return false;
  return sliceable(k0.ease) && (k0.segmentSelected || isSliced(k0.ease));
}

export interface SliceRail {
  /** Left edge of the rail. */
  readonly x: number;
  readonly width: number;
  /** y at u = 0 (segment start) and u = 1 (segment end). */
  readonly y0: number;
  readonly y1: number;
  readonly left: number;
  readonly right: number;
  /** Handle centres. */
  readonly leftHandle: Point;
  readonly rightHandle: Point;
}

/** Rail geometry of segment i (beside the segment, inside the lane). */
export function sliceRailOf(
  lane: CurveLaneView,
  i: number,
  axis: Pick<TimeAxis, 'yAtBeat'>,
  time: CurveTimeMap,
): SliceRail | null {
  const k0 = lane.keys[i];
  const k1 = lane.keys[i + 1];
  if (k0 === undefined || k1 === undefined || k0.ease.kind !== 'tween') return null;
  const xa = lane.axis.valueToX(k0.vOut);
  const xb = lane.axis.valueToX(k1.vIn);
  const laneRight = lane.x0 + lane.width;
  let x = Math.max(xa, xb) + SLICE_RAIL_GAP;
  if (x + SLICE_RAIL_W > laneRight - 2) {
    const leftX = Math.min(xa, xb) - SLICE_RAIL_GAP - SLICE_RAIL_W;
    x = leftX >= lane.x0 + 2 ? leftX : Math.max(lane.x0 + 2, laneRight - 2 - SLICE_RAIL_W);
  }
  const { left, right } = k0.ease;
  const yAt = (u: number): number => axis.yAtBeat(beatAtProgress(time, k0, k1, u));
  const cx = x + SLICE_RAIL_W / 2;
  return {
    x,
    width: SLICE_RAIL_W,
    y0: axis.yAtBeat(k0.beat),
    y1: axis.yAtBeat(k1.beat),
    left,
    right,
    leftHandle: { x: cx, y: yAt(left) },
    rightHandle: { x: cx, y: yAt(right) },
  };
}

/** Slice parameter u in [0, 1] under a pointer y on segment i's rail. */
export function sliceParamAt(
  lane: CurveLaneView,
  i: number,
  py: number,
  axis: Pick<TimeAxis, 'beatAtY'>,
  time: CurveTimeMap,
): number {
  const k0 = lane.keys[i];
  const k1 = lane.keys[i + 1];
  if (k0 === undefined || k1 === undefined) return 0;
  return Math.min(1, Math.max(0, progressAtBeat(time, k0, k1, axis.beatAtY(py))));
}

// ---------------------------------------------------------------------------------------------------------------
// Hit-testing
// ---------------------------------------------------------------------------------------------------------------

export type CurveHitPart =
  | { readonly part: 'key'; readonly index: number }
  | { readonly part: 'keyIn' | 'keyOut'; readonly index: number }
  | { readonly part: 'bezier'; readonly index: number; readonly handle: 1 | 2 }
  | { readonly part: 'slice'; readonly index: number; readonly side: 'left' | 'right' }
  | { readonly part: 'segment'; readonly index: number }
  | { readonly part: 'empty' };

/** A TimelineHit with the curve editor's details (the host hands it back to onPointerDown untouched). */
export interface CurveHit extends TimelineHit {
  readonly lane: string;
  readonly detail: CurveHitPart;
}

function hitKind(p: CurveHitPart): TimelineHit['kind'] {
  switch (p.part) {
    case 'key':
      return 'key';
    case 'keyIn':
      return 'keyIn';
    case 'keyOut':
      return 'keyOut';
    case 'bezier':
      return 'bezierHandle';
    case 'slice':
      return 'clipHandle';
    case 'segment':
      return 'segment';
    case 'empty':
      return 'empty';
  }
}

/**
 * True when the arrive half of jump key i is fixed: after a Stay segment the key arrives at the stayed value (the
 * core refuses to change it), so that half only moves the key in time.
 */
export function arriveFixed(lane: Pick<CurveLaneView, 'keys'>, i: number): boolean {
  return i > 0 && lane.keys[i - 1]?.ease.kind === 'hold';
}

function hitCursor(p: CurveHitPart, editable: boolean, fixedArrive: boolean): string {
  if (!editable) return p.part === 'empty' ? 'default' : 'pointer';
  switch (p.part) {
    case 'key':
      return 'move';
    case 'keyIn':
      // Vertical only: the value it arrives at is the stayed value.
      return fixedArrive ? 'ns-resize' : 'ew-resize';
    case 'keyOut':
      return 'ew-resize';
    case 'bezier':
      return 'grab';
    case 'slice':
      return 'ns-resize';
    case 'segment':
      return 'pointer';
    case 'empty':
      return 'crosshair';
  }
}

function makeHit(lane: CurveLaneView, part: CurveHitPart): CurveHit {
  const index = part.part === 'empty' ? -1 : part.index;
  const key = index >= 0 ? lane.keys[index]?.id : undefined;
  return {
    kind: hitKind(part),
    ...(key === undefined ? {} : { key }),
    cursor: hitCursor(part, lane.editable, part.part === 'keyIn' && arriveFixed(lane, part.index)),
    lane: lane.id,
    detail: part,
  };
}

/** Distance from (px, py) to the segment (ax, ay)-(bx, by). */
export function distToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 > 0 ? ((px - ax) * dx + (py - ay) * dy) / len2 : 0;
  t = Math.min(1, Math.max(0, t));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/** Smallest distance from a point to the sampled curve between two beats (the curve's polyline). */
export function distToCurve(
  curve: SampledCurve,
  axis: Pick<TimeAxis, 'yAtBeat'>,
  valueToX: (v: number) => number,
  beatLo: number,
  beatHi: number,
  px: number,
  py: number,
): number {
  const i0 = Math.max(0, Math.floor((beatLo - curve.beat0) / curve.step));
  const i1 = Math.min(curve.values.length - 1, Math.ceil((beatHi - curve.beat0) / curve.step));
  let best = Number.POSITIVE_INFINITY;
  let prev: Point | null = null;
  for (let i = i0; i <= i1; i++) {
    const v = curve.values[i];
    const beat = curve.beat0 + i * curve.step;
    if (v === undefined || !Number.isFinite(v) || beat < beatLo - curve.step || beat > beatHi + curve.step) {
      prev = null;
      continue;
    }
    const p = { x: valueToX(v), y: axis.yAtBeat(Math.min(beatHi, Math.max(beatLo, beat))) };
    if (prev !== null) best = Math.min(best, distToSegment(px, py, prev.x, prev.y, p.x, p.y));
    else best = Math.min(best, Math.hypot(px - p.x, py - p.y));
    prev = p;
  }
  return best;
}

/** Index of the last key at or before `beat` (-1 before the first key). */
export function keyIndexAtBeat(keys: readonly CurveKeyView[], beat: number): number {
  let lo = 0;
  let hi = keys.length - 1;
  let best = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if ((keys[mid] as CurveKeyView).beat <= beat) {
      best = mid;
      lo = mid + 1;
    } else hi = mid - 1;
  }
  return best;
}

/**
 * What is under (x, y) in a lane, in priority order: Bezier handles, slice handles, keys (the two halves of a jump
 * key separately), the curve of a segment, then empty space. Null when x is outside the lane.
 */
export function hitTestLane(
  lane: CurveLaneView,
  x: number,
  y: number,
  axis: Pick<TimeAxis, 'yAtBeat' | 'beatAtY'>,
  time: CurveTimeMap,
): CurveHit | null {
  if (x < lane.x0 || x > lane.x0 + lane.width) return null;
  const keys = lane.keys;
  const va = lane.axis;
  // Only keys near the pointer's beat can be hit (with a generous margin for handles).
  const margin = Math.max(KEY_PICK_PX, 60) / Math.max(1e-9, Math.abs(axis.yAtBeat(0) - axis.yAtBeat(1)));
  const beat = axis.beatAtY(y);
  let first = Math.max(0, keyIndexAtBeat(keys, beat - margin) - 1);
  const last = Math.min(keys.length - 1, keyIndexAtBeat(keys, beat + margin) + 1);
  // A segment that spans the whole view has its keys far away: also look at the segment under the pointer.
  const under = keyIndexAtBeat(keys, beat);
  if (under >= 0 && under < first) first = under;

  if (lane.editable) {
    for (let i = first; i <= last; i++) {
      if (lane.bezierHandles) {
        const h = bezierHandlePoints(lane, i, axis, time);
        if (h !== null && Math.abs(h.p3.y - h.p0.y) >= MIN_HANDLE_SEGMENT_PX) {
          if (Math.hypot(x - h.h1.x, y - h.h1.y) <= BEZIER_HANDLE_R + 3) {
            return makeHit(lane, { part: 'bezier', index: i, handle: 1 });
          }
          if (Math.hypot(x - h.h2.x, y - h.h2.y) <= BEZIER_HANDLE_R + 3) {
            return makeHit(lane, { part: 'bezier', index: i, handle: 2 });
          }
        }
      }
      if (showsSliceRail(lane, i)) {
        const r = sliceRailOf(lane, i, axis, time);
        if (r !== null && Math.abs(r.y1 - r.y0) >= MIN_HANDLE_SEGMENT_PX) {
          const within = (h: Point): boolean =>
            Math.abs(x - h.x) <= SLICE_HANDLE_W / 2 + 2 && Math.abs(y - h.y) <= SLICE_HANDLE_H / 2 + 3;
          if (within(r.leftHandle)) return makeHit(lane, { part: 'slice', index: i, side: 'left' });
          if (within(r.rightHandle)) return makeHit(lane, { part: 'slice', index: i, side: 'right' });
        }
      }
    }
  }

  // Keys: nearest within the pick radius.
  let bestKey: CurveHitPart | null = null;
  let bestD = KEY_PICK_PX + 0.5;
  for (let i = first; i <= last; i++) {
    const k = keys[i];
    if (k === undefined) continue;
    const ky = axis.yAtBeat(k.beat);
    if (Math.abs(ky - y) > KEY_PICK_PX) continue;
    if (k.vIn !== k.vOut) {
      const dIn = Math.hypot(x - va.valueToX(k.vIn), y - ky);
      const dOut = Math.hypot(x - va.valueToX(k.vOut), y - ky);
      if (dIn < bestD) {
        bestD = dIn;
        bestKey = { part: 'keyIn', index: i };
      }
      if (dOut < bestD) {
        bestD = dOut;
        bestKey = { part: 'keyOut', index: i };
      }
    } else {
      const d = Math.hypot(x - va.valueToX(k.vOut), y - ky);
      if (d < bestD) {
        bestD = d;
        bestKey = { part: 'key', index: i };
      }
    }
  }
  if (bestKey !== null) return makeHit(lane, bestKey);

  // Segment under the pointer: its curve (or the dashed Stay line) within the pick distance.
  const si = keyIndexAtBeat(keys, beat);
  const k0 = keys[si];
  const k1 = keys[si + 1];
  if (k0 !== undefined && k1 !== undefined) {
    let d: number;
    if (k0.ease.kind === 'hold') {
      d = Math.abs(x - va.valueToX(k0.vOut));
    } else if (lane.curve !== null) {
      d = distToCurve(lane.curve, axis, va.valueToX, k0.beat, k1.beat, x, y);
    } else {
      d = distToSegment(x, y, va.valueToX(k0.vOut), axis.yAtBeat(k0.beat), va.valueToX(k1.vIn), axis.yAtBeat(k1.beat));
    }
    if (d <= CURVE_PICK_PX) return makeHit(lane, { part: 'segment', index: si });
  }
  return makeHit(lane, { part: 'empty' });
}

/** The lane containing column x, or null. */
export function laneAtX(scene: CurveScene, x: number): CurveLaneView | null {
  for (const lane of scene.lanes) if (x >= lane.x0 && x <= lane.x0 + lane.width) return lane;
  return null;
}

/** Hit-test over every lane of a scene. */
export function hitTestScene(
  scene: CurveScene,
  x: number,
  y: number,
  axis: Pick<TimeAxis, 'yAtBeat' | 'beatAtY'>,
): CurveHit | null {
  const lane = laneAtX(scene, x);
  return lane === null ? null : hitTestLane(lane, x, y, axis, scene.time);
}

/**
 * Marquee answer of a scene: keys whose diamond (either half of a jump key) lies in the rectangle, and segments whose
 * two keys both lie in its beat range and whose curve enters its x range.
 */
export function itemsInSceneRect(
  scene: CurveScene,
  beat0: number,
  beat1: number,
  x0: number,
  x1: number,
): TimelineRectItems {
  const bLo = Math.min(beat0, beat1);
  const bHi = Math.max(beat0, beat1);
  const xLo = Math.min(x0, x1);
  const xHi = Math.max(x0, x1);
  const keys: KeyId[] = [];
  const segments: KeyId[] = [];
  for (const lane of scene.lanes) {
    if (!lane.editable || lane.x0 + lane.width < xLo || lane.x0 > xHi) continue;
    const va = lane.axis;
    for (let i = 0; i < lane.keys.length; i++) {
      const k = lane.keys[i] as CurveKeyView;
      if (k.beat < bLo || k.beat > bHi) continue;
      const xin = va.valueToX(k.vIn);
      const xout = va.valueToX(k.vOut);
      if ((xin >= xLo && xin <= xHi) || (xout >= xLo && xout <= xHi)) keys.push(k.id);
      const next = lane.keys[i + 1];
      if (next !== undefined && next.beat <= bHi) {
        const a = va.valueToX(k.vOut);
        const b = va.valueToX(next.vIn);
        if (Math.max(a, b) >= xLo && Math.min(a, b) <= xHi) segments.push(k.id);
      }
    }
  }
  return { keys, segments };
}

// ---------------------------------------------------------------------------------------------------------------
// Drawing helpers
// ---------------------------------------------------------------------------------------------------------------

function sampleBeat(curve: SampledCurve, i: number): number {
  return curve.beat0 + i * curve.step;
}

/**
 * Strokes the samples of `curve` with beats strictly inside (b0, b1), starting at `start` and ending at `end` when
 * given (the exact key positions, so a jump never shows as a slanted line). NaN samples break the line.
 */
export function strokeCurveRange(
  ctx: CanvasRenderingContext2D,
  curve: SampledCurve,
  axis: Pick<TimeAxis, 'yAtBeat'>,
  valueToX: (v: number) => number,
  b0: number,
  b1: number,
  start: Point | null,
  end: Point | null,
): void {
  ctx.beginPath();
  let pen = false;
  if (start !== null) {
    ctx.moveTo(start.x, start.y);
    pen = true;
  }
  const i0 = Math.max(0, Math.floor((b0 - curve.beat0) / curve.step));
  const i1 = Math.min(curve.values.length - 1, Math.ceil((b1 - curve.beat0) / curve.step));
  for (let i = i0; i <= i1; i++) {
    const beat = sampleBeat(curve, i);
    if (!(beat > b0 && beat < b1)) continue;
    const v = curve.values[i];
    if (v === undefined || !Number.isFinite(v)) {
      pen = false;
      continue;
    }
    const x = valueToX(v);
    const y = axis.yAtBeat(beat);
    if (pen) ctx.lineTo(x, y);
    else {
      ctx.moveTo(x, y);
      pen = true;
    }
  }
  if (end !== null) {
    if (pen) ctx.lineTo(end.x, end.y);
    else ctx.moveTo(end.x, end.y);
  }
  ctx.stroke();
}

/** Strokes a whole sampled curve inside a beat window (ghosts, Σ). */
export function strokeWholeCurve(
  ctx: CanvasRenderingContext2D,
  curve: SampledCurve,
  axis: Pick<TimeAxis, 'yAtBeat'>,
  valueToX: (v: number) => number,
): void {
  strokeCurveRange(
    ctx,
    curve,
    axis,
    valueToX,
    curve.beat0 - curve.step,
    curve.beat0 + curve.values.length * curve.step,
    null,
    null,
  );
}

function withAlpha(ctx: CanvasRenderingContext2D, alpha: number, fn: () => void): void {
  ctx.save();
  ctx.globalAlpha *= alpha;
  fn();
  ctx.restore();
}

function clipLane(ctx: CanvasRenderingContext2D, lane: CurveLaneView, height: number): void {
  ctx.beginPath();
  ctx.rect(lane.x0, 0, lane.width, height);
  ctx.clip();
}

/** Text with a background chip (readable over curves). */
export function drawChip(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  theme: CurveTheme,
  align: 'left' | 'right' | 'center' = 'left',
  color?: string,
): void {
  if (text === '') return;
  ctx.save();
  ctx.font = canvasFont(theme, 10, 'mono');
  ctx.textBaseline = 'middle';
  const w = ctx.measureText(text).width;
  const width = Number.isFinite(w) ? w : text.length * 6;
  const left = align === 'left' ? x : align === 'right' ? x - width - 6 : x - (width + 6) / 2;
  ctx.globalAlpha *= 0.85;
  ctx.fillStyle = theme.surface;
  ctx.fillRect(left, y - 7, width + 6, 14);
  ctx.globalAlpha = 1;
  ctx.fillStyle = color ?? theme.text;
  ctx.textAlign = 'left';
  ctx.fillText(text, left + 3, y);
  ctx.restore();
}

// ---------------------------------------------------------------------------------------------------------------
// Layers
// ---------------------------------------------------------------------------------------------------------------

/** Static layer: background and the beat grid (sub-beat, beat and bar lines), the same look as the other columns. */
export function drawCurveStatic(ctx: CanvasRenderingContext2D, axis: TimeAxis, width: number, theme: CurveTheme): void {
  ctx.save();
  ctx.fillStyle = theme.background;
  ctx.fillRect(0, 0, width, axis.heightPx);
  ctx.restore();
  const ac = axisContext(axis);
  drawBeatGrid(ctx, axis, width, {
    div: ac?.view.snapDiv ?? 4,
    meter: ac?.meter ?? null,
    theme,
    subAlpha: 0.22,
    beatAlpha: 0.7,
  });
}

/** Value grid, tick labels and the zero line of a lane. */
/** Raw tick values of a lane's value grid (round in display units) and the raw step between them. */
export function laneValueTicks(lane: Pick<CurveLaneView, 'axis' | 'displayScale'>): {
  readonly step: number;
  readonly ticks: number[];
} {
  const va = lane.axis;
  const scale = Number.isFinite(lane.displayScale) && lane.displayScale !== 0 ? lane.displayScale : 1;
  const count = Math.max(2, Math.floor((va.x1 - va.x0) / 70));
  const a = va.lo * scale;
  const b = va.hi * scale;
  const { step, ticks } = niceTicks(Math.min(a, b), Math.max(a, b), count);
  return { step: Math.abs(step / scale), ticks: ticks.map((t) => (t === 0 ? 0 : t / scale)) };
}

function drawValueGrid(ctx: CanvasRenderingContext2D, lane: CurveLaneView, height: number, theme: CurveTheme): void {
  const va = lane.axis;
  const { step, ticks } = laneValueTicks(lane);
  ctx.save();
  clipLane(ctx, lane, height);
  ctx.lineWidth = 1;
  for (const v of ticks) {
    const x = Math.round(va.valueToX(v)) + 0.5;
    ctx.strokeStyle = v === 0 ? theme.curveGuide : theme.gridSub;
    ctx.globalAlpha = v === 0 ? 1 : 0.5;
    if (v !== 0) ctx.setLineDash([2, 4]);
    ctx.beginPath();
    ctx.moveTo(x, VALUE_BAND_PX);
    ctx.lineTo(x, height);
    ctx.stroke();
    ctx.setLineDash([]);
  }
  // Labels in the top band.
  ctx.globalAlpha = 1;
  ctx.fillStyle = theme.gutterBackground;
  ctx.fillRect(lane.x0, 0, lane.width, VALUE_BAND_PX);
  ctx.font = canvasFont(theme, 9, 'mono');
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.fillStyle = theme.textMuted;
  let lastRight = Number.NEGATIVE_INFINITY;
  for (const v of ticks) {
    const x = va.valueToX(v);
    const text = lane.formatValue(v, step);
    const w = ctx.measureText(text).width;
    const tw = Number.isFinite(w) ? w : text.length * 5.5;
    if (x - tw / 2 < lastRight + 4) continue;
    ctx.fillText(text, x, VALUE_BAND_PX / 2);
    lastRight = x + tw / 2;
  }
  ctx.restore();
}

function drawHatch(ctx: CanvasRenderingContext2D, lane: CurveLaneView, height: number, theme: CurveTheme): void {
  if (lane.hatchBelow === null) return;
  const x = lane.axis.valueToX(lane.hatchBelow);
  const left = lane.x0;
  const right = Math.min(lane.x0 + lane.width, x);
  if (right <= left) return;
  ctx.save();
  ctx.strokeStyle = theme.danger;
  ctx.globalAlpha = 0.35;
  hatchRect(ctx, left, VALUE_BAND_PX, right - left, height - VALUE_BAND_PX, 7);
  ctx.restore();
}

function keyPoint(lane: CurveLaneView, axis: Pick<TimeAxis, 'yAtBeat'>, k: CurveKeyView, side: 'in' | 'out'): Point {
  return { x: lane.axis.valueToX(side === 'in' ? k.vIn : k.vOut), y: axis.yAtBeat(k.beat) };
}

/** The lane's own curve, segment by segment (exact key endpoints, dashed Stay lines, dashed jumps). */
function drawLaneCurve(ctx: CanvasRenderingContext2D, lane: CurveLaneView, axis: TimeAxis, theme: CurveTheme): void {
  const curve = lane.curve;
  const keys = lane.keys;
  const va = lane.axis;
  const [vb0, vb1] = axis.range;
  const first = keys[0];
  const last = keys[keys.length - 1];
  ctx.save();
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';
  if (first === undefined || last === undefined) {
    if (curve !== null) {
      ctx.strokeStyle = theme.curve;
      ctx.lineWidth = 1.5;
      strokeWholeCurve(ctx, curve, axis, va.valueToX);
    }
    ctx.restore();
    return;
  }
  // Lead-in before the first key (extrapolated / default by the engine), fainter.
  if (curve !== null && first.beat > vb0) {
    withAlpha(ctx, 0.45, () => {
      ctx.strokeStyle = theme.curve;
      ctx.lineWidth = 1.25;
      ctx.setLineDash([5, 4]);
      strokeCurveRange(
        ctx,
        curve,
        axis,
        va.valueToX,
        vb0 - curve.step,
        first.beat,
        null,
        keyPoint(lane, axis, first, 'in'),
      );
      ctx.setLineDash([]);
    });
  }
  const guide = lane.curveStyle === 'guide';
  const baseColor = guide ? theme.textMuted : theme.curve;
  for (let i = 0; i < keys.length - 1; i++) {
    const k0 = keys[i] as CurveKeyView;
    const k1 = keys[i + 1] as CurveKeyView;
    if (k1.beat < vb0 || k0.beat > vb1) continue;
    const selected = k0.segmentSelected;
    ctx.strokeStyle = selected ? theme.accent : baseColor;
    ctx.lineWidth = selected ? 2.5 : guide ? 1 : 1.5;
    ctx.setLineDash(guide && !selected ? [4, 3] : []);
    if (k0.ease.kind === 'hold') {
      const x = va.valueToX(k0.vOut);
      Glyphs.holdSegment(ctx, x, axis.yAtBeat(k0.beat), axis.yAtBeat(k1.beat));
    } else if (curve !== null) {
      strokeCurveRange(
        ctx,
        curve,
        axis,
        va.valueToX,
        k0.beat,
        k1.beat,
        keyPoint(lane, axis, k0, 'out'),
        keyPoint(lane, axis, k1, 'in'),
      );
    } else {
      ctx.beginPath();
      const a = keyPoint(lane, axis, k0, 'out');
      const b = keyPoint(lane, axis, k1, 'in');
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    }
  }
  ctx.setLineDash(guide ? [4, 3] : []);
  // After the last key the value holds.
  if (last.beat < vb1) {
    const x = va.valueToX(last.vOut);
    ctx.strokeStyle = baseColor;
    ctx.lineWidth = guide ? 1 : 1.5;
    ctx.beginPath();
    ctx.moveTo(x, axis.yAtBeat(last.beat));
    ctx.lineTo(x, axis.yAtBeat(vb1 + 1));
    ctx.stroke();
  }
  // Jumps: dashed horizontal step between the arrive and leave values.
  ctx.setLineDash([3, 3]);
  ctx.lineWidth = 1;
  ctx.strokeStyle = baseColor;
  for (const k of keys) {
    if (k.vIn === k.vOut || k.beat < vb0 || k.beat > vb1) continue;
    const y = axis.yAtBeat(k.beat);
    ctx.beginPath();
    ctx.moveTo(va.valueToX(k.vIn), y);
    ctx.lineTo(va.valueToX(k.vOut), y);
    ctx.stroke();
  }
  ctx.restore();
}

function drawBezierHandles(
  ctx: CanvasRenderingContext2D,
  lane: CurveLaneView,
  axis: TimeAxis,
  time: CurveTimeMap,
  theme: CurveTheme,
): void {
  if (!lane.bezierHandles || !lane.editable) return;
  const [vb0, vb1] = axis.range;
  ctx.save();
  for (let i = 0; i < lane.keys.length - 1; i++) {
    const k0 = lane.keys[i] as CurveKeyView;
    const k1 = lane.keys[i + 1] as CurveKeyView;
    if (k0.ease.kind !== 'bezier' || k1.beat < vb0 || k0.beat > vb1) continue;
    const h = bezierHandlePoints(lane, i, axis, time);
    if (h === null || Math.abs(h.p3.y - h.p0.y) < MIN_HANDLE_SEGMENT_PX) continue;
    const strong = k0.segmentSelected || k0.selected || k1.selected;
    ctx.globalAlpha = strong ? 1 : 0.55;
    ctx.strokeStyle = theme.textMuted;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(h.p0.x, h.p0.y);
    ctx.lineTo(h.h1.x, h.h1.y);
    ctx.moveTo(h.p3.x, h.p3.y);
    ctx.lineTo(h.h2.x, h.h2.y);
    ctx.stroke();
    for (const p of [h.h1, h.h2]) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, BEZIER_HANDLE_R, 0, Math.PI * 2);
      ctx.fillStyle = strong ? theme.accent : theme.surface;
      ctx.fill();
      ctx.strokeStyle = theme.accent;
      ctx.stroke();
    }
  }
  ctx.restore();
}

/** Whole-curve samples of a tween for its slice rail: u in [0, 1] -> normalised f(u) in [0, 1]. */
export type WholeCurveFn = (easingType: number, u: number) => number;

function drawSliceRails(
  ctx: CanvasRenderingContext2D,
  lane: CurveLaneView,
  axis: TimeAxis,
  time: CurveTimeMap,
  theme: CurveTheme,
  whole: WholeCurveFn,
): void {
  if (!lane.editable) return;
  const [vb0, vb1] = axis.range;
  ctx.save();
  for (let i = 0; i < lane.keys.length - 1; i++) {
    if (!showsSliceRail(lane, i)) continue;
    const k0 = lane.keys[i] as CurveKeyView;
    const k1 = lane.keys[i + 1] as CurveKeyView;
    if (k1.beat < vb0 || k0.beat > vb1 || k0.ease.kind !== 'tween') continue;
    const r = sliceRailOf(lane, i, axis, time);
    if (r === null || Math.abs(r.y1 - r.y0) < MIN_HANDLE_SEGMENT_PX) continue;
    const id = k0.ease.id;
    // Normalise the whole curve over its own min/max so it fits the rail width.
    const ys: number[] = [];
    let lo = Number.POSITIVE_INFINITY;
    let hi = Number.NEGATIVE_INFINITY;
    for (let s = 0; s <= RAIL_SAMPLES; s++) {
      const f = whole(id, s / RAIL_SAMPLES);
      ys.push(f);
      if (Number.isFinite(f)) {
        lo = Math.min(lo, f);
        hi = Math.max(hi, f);
      }
    }
    const span = hi > lo ? hi - lo : 1;
    const xOf = (f: number): number => r.x + 2 + ((f - lo) / span) * (r.width - 4);
    // Rail background.
    ctx.globalAlpha = 0.75;
    ctx.fillStyle = theme.surface;
    ctx.fillRect(r.x, Math.min(r.y0, r.y1), r.width, Math.abs(r.y1 - r.y0));
    ctx.globalAlpha = 1;
    // Whole curve (faint) and the used part (accent).
    for (const used of [false, true]) {
      ctx.beginPath();
      let pen = false;
      for (let s = 0; s <= RAIL_SAMPLES; s++) {
        const u = s / RAIL_SAMPLES;
        if (used && (u < r.left - 1e-9 || u > r.right + 1e-9)) {
          pen = false;
          continue;
        }
        const f = ys[s] ?? Number.NaN;
        if (!Number.isFinite(f)) {
          pen = false;
          continue;
        }
        const px = xOf(f);
        const py = axis.yAtBeat(beatAtProgress(time, k0, k1, u));
        if (pen) ctx.lineTo(px, py);
        else {
          ctx.moveTo(px, py);
          pen = true;
        }
      }
      ctx.strokeStyle = used ? theme.accent : theme.textSubtle;
      ctx.lineWidth = used ? 1.5 : 1;
      ctx.stroke();
    }
    // Handles.
    for (const h of [r.leftHandle, r.rightHandle]) {
      ctx.fillStyle = theme.accent;
      ctx.fillRect(h.x - SLICE_HANDLE_W / 2, h.y - SLICE_HANDLE_H / 2, SLICE_HANDLE_W, SLICE_HANDLE_H);
      ctx.strokeStyle = theme.background;
      ctx.lineWidth = 1;
      ctx.strokeRect(h.x - SLICE_HANDLE_W / 2, h.y - SLICE_HANDLE_H / 2, SLICE_HANDLE_W, SLICE_HANDLE_H);
    }
  }
  ctx.restore();
}

function drawKeys(ctx: CanvasRenderingContext2D, lane: CurveLaneView, axis: TimeAxis, theme: CurveTheme): void {
  const [vb0, vb1] = axis.range;
  const va = lane.axis;
  ctx.save();
  ctx.lineWidth = 1.25;
  lane.keys.forEach((k, i) => {
    if (k.beat < vb0 - 1 || k.beat > vb1 + 1) return;
    const y = axis.yAtBeat(k.beat);
    ctx.fillStyle = k.selected ? theme.accent : theme.curve;
    ctx.strokeStyle = k.selected ? theme.text : theme.curve;
    if (k.vIn !== k.vOut) {
      // A fixed arrive half (after a Stay segment) is drawn fainter: it follows the stayed value.
      if (arriveFixed(lane, i)) {
        withAlpha(ctx, 0.5, () => {
          Glyphs.jumpKey(ctx, va.valueToX(k.vIn), y, KEY_SIZE + 2, true);
        });
      } else {
        Glyphs.jumpKey(ctx, va.valueToX(k.vIn), y, KEY_SIZE + 2, true);
      }
      Glyphs.jumpKey(ctx, va.valueToX(k.vOut), y, KEY_SIZE + 2, false);
    } else {
      if (!k.selected) {
        // Hollow: fill with the background so the curve does not show through.
        ctx.fillStyle = theme.background;
        Glyphs.keyDiamond(ctx, va.valueToX(k.vOut), y, KEY_SIZE + 1, true);
      } else {
        Glyphs.keyDiamond(ctx, va.valueToX(k.vOut), y, KEY_SIZE + 1, true);
      }
    }
  });
  ctx.restore();
}

function drawLabels(ctx: CanvasRenderingContext2D, lane: CurveLaneView, axis: TimeAxis, theme: CurveTheme): void {
  const [vb0, vb1] = axis.range;
  const va = lane.axis;
  for (let i = 0; i < lane.keys.length; i++) {
    const k = lane.keys[i] as CurveKeyView;
    const next = lane.keys[i + 1];
    const seg = lane.segmentLabels[i] ?? null;
    if (seg !== null && next !== undefined && next.beat >= vb0 && k.beat <= vb1) {
      const y0 = axis.yAtBeat(k.beat);
      const y1 = axis.yAtBeat(next.beat);
      if (Math.abs(y1 - y0) >= MIN_LABEL_SEGMENT_PX) {
        const ym = Math.min(axis.heightPx - 10, Math.max(VALUE_BAND_PX + 10, (y0 + y1) / 2));
        const xm = (va.valueToX(k.vOut) + va.valueToX(next.vIn)) / 2;
        const right = xm < lane.x0 + lane.width / 2;
        drawChip(ctx, seg, right ? xm + 10 : xm - 10, ym, theme, right ? 'left' : 'right', theme.textMuted);
      }
    }
    const label = lane.keyLabels[i] ?? null;
    if (label !== null && k.beat >= vb0 && k.beat <= vb1) {
      const x = va.valueToX(Math.max(k.vIn, k.vOut));
      const right = x < lane.x0 + lane.width - 90;
      drawChip(
        ctx,
        label,
        right ? x + 9 : va.valueToX(Math.min(k.vIn, k.vOut)) - 9,
        axis.yAtBeat(k.beat) - 10,
        theme,
        right ? 'left' : 'right',
      );
    }
  }
}

export interface ContentExtras {
  /** Draws the speed view of a lane (floorOverlay.ts), before keys. */
  readonly speed?: (ctx: CanvasRenderingContext2D, lane: CurveLaneView, axis: TimeAxis, theme: CurveTheme) => void;
  readonly wholeCurve: WholeCurveFn;
}

/** Content layer: per lane the value grid, hatching, ghosts, Σ, the curve, handles, keys and labels. */
export function drawCurveContent(
  ctx: CanvasRenderingContext2D,
  axis: TimeAxis,
  scene: CurveScene,
  theme: CurveTheme,
  extras: ContentExtras,
): void {
  const h = axis.heightPx;
  for (const lane of scene.lanes) {
    ctx.save();
    clipLane(ctx, lane, h);
    if (lane.focused) {
      ctx.save();
      ctx.fillStyle = theme.accentMuted;
      ctx.globalAlpha = 0.35;
      ctx.fillRect(lane.x0, 0, lane.width, h);
      ctx.restore();
    }
    drawHatch(ctx, lane, h, theme);
    drawValueGrid(ctx, lane, h, theme);
    for (const g of lane.ghosts) {
      withAlpha(ctx, GHOST_ALPHA, () => {
        ctx.strokeStyle = theme.curve;
        ctx.lineWidth = 1.5;
        strokeWholeCurve(ctx, g, axis, lane.axis.valueToX);
      });
    }
    if (lane.sigma !== null) {
      const sigma = lane.sigma;
      withAlpha(ctx, lane.id === 'sum' ? 1 : 0.8, () => {
        ctx.strokeStyle = lane.id === 'sum' ? theme.curve : theme.textMuted;
        ctx.lineWidth = lane.id === 'sum' ? 1.75 : 1.25;
        if (lane.id !== 'sum') ctx.setLineDash([6, 4]);
        strokeWholeCurve(ctx, sigma, axis, lane.axis.valueToX);
        ctx.setLineDash([]);
      });
    }
    if (lane.speed !== null) extras.speed?.(ctx, lane, axis, theme);
    drawLaneCurve(ctx, lane, axis, theme);
    drawBezierHandles(ctx, lane, axis, scene.time, theme);
    drawSliceRails(ctx, lane, axis, scene.time, theme, extras.wholeCurve);
    drawKeys(ctx, lane, axis, theme);
    drawLabels(ctx, lane, axis, theme);
    if (lane.caption != null && lane.caption !== '') {
      drawChip(ctx, lane.caption, lane.x0 + 4, VALUE_BAND_PX + 10, theme, 'left', theme.textMuted);
    }
    if (lane.emptyText !== null) {
      ctx.font = canvasFont(theme, 11, 'sans');
      ctx.fillStyle = theme.textMuted;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      wrapText(ctx, lane.emptyText, lane.x0 + lane.width / 2, h / 2, lane.width - 24, 14);
    }
    ctx.restore();
    // Lane separator.
    ctx.save();
    ctx.strokeStyle = theme.divider;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(Math.round(lane.x0 + lane.width) - 0.5, 0);
    ctx.lineTo(Math.round(lane.x0 + lane.width) - 0.5, h);
    ctx.stroke();
    ctx.restore();
  }
}

/** Centred multi-line text wrapped at `maxWidth`. */
export function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  cx: number,
  cy: number,
  maxWidth: number,
  lineHeight: number,
): void {
  const words = text.split(/\s+/).filter((w) => w !== '');
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    const next = line === '' ? w : `${line} ${w}`;
    const m = ctx.measureText(next).width;
    const width = Number.isFinite(m) ? m : next.length * 6;
    if (width > maxWidth && line !== '') {
      lines.push(line);
      line = w;
    } else line = next;
  }
  if (line !== '') lines.push(line);
  const top = cy - ((lines.length - 1) * lineHeight) / 2;
  lines.forEach((l, i) => {
    ctx.fillText(l, cx, top + i * lineHeight);
  });
}

// ---------------------------------------------------------------------------------------------------------------
// Overlay
// ---------------------------------------------------------------------------------------------------------------

export interface CurveOverlayState {
  /** Hover crosshair and readout. */
  readonly hover: { readonly x: number; readonly y: number; readonly lane: string; readonly text: string } | null;
  /** Live drag readout ("12:1/4 = 135"). */
  readonly drag: { readonly x: number; readonly y: number; readonly text: string } | null;
  /** Value readout per lane at the playhead (dot on the curve + text). */
  readonly playhead: ReadonlyArray<{ readonly lane: string; readonly value: number; readonly text: string }>;
  /** Extra readout under the playhead (speed view: "Notes cross the screen in 0.75 s"). */
  readonly note: string | null;
  /** Hint while a handle refuses a value (degenerate slice). */
  readonly warning: { readonly x: number; readonly y: number; readonly text: string } | null;
}

export const EMPTY_OVERLAY: CurveOverlayState = Object.freeze({
  hover: null,
  drag: null,
  playhead: Object.freeze([]),
  note: null,
  warning: null,
});

/**
 * Overlay layer: playhead readouts, hover crosshair, drag readout and warnings. Draws over what is there and never
 * clears: the timeline host composes the static and content strips onto the column's one surface first and then
 * calls the overlay on that surface (W3-03 scroll cache; a clear here blanked the whole column, CR-W3-05-2 item 3).
 */
export function drawCurveOverlay(
  ctx: CanvasRenderingContext2D,
  axis: TimeAxis,
  width: number,
  scene: CurveScene,
  state: CurveOverlayState,
  theme: CurveTheme,
): void {
  const h = axis.heightPx;
  const ac = axisContext(axis);
  if (ac !== null) {
    const py = axis.yAtBeat(ac.playheadBeat);
    if (py >= 0 && py <= h) {
      for (const p of state.playhead) {
        const lane = scene.lanes.find((l) => l.id === p.lane);
        if (lane === undefined || !Number.isFinite(p.value)) continue;
        const x = lane.axis.valueToX(p.value);
        if (x < lane.x0 || x > lane.x0 + lane.width) continue;
        ctx.save();
        ctx.fillStyle = theme.playhead;
        ctx.beginPath();
        ctx.arc(x, py, 3.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        const right = x < lane.x0 + lane.width - 80;
        drawChip(ctx, p.text, right ? x + 7 : x - 7, py - 11, theme, right ? 'left' : 'right', theme.playhead);
      }
      if (state.note !== null) {
        // On the lane that draws the speed the note describes (the Σ lane in the channel view).
        const lane = scene.lanes.find((l) => l.speed !== null) ?? scene.lanes[0];
        if (lane !== undefined) drawChip(ctx, state.note, lane.x0 + 4, py + 11, theme, 'left');
      }
    }
  }
  if (state.hover !== null) {
    const hv = state.hover;
    const lane = scene.lanes.find((l) => l.id === hv.lane);
    if (lane !== undefined) {
      ctx.save();
      ctx.strokeStyle = theme.textSubtle;
      ctx.globalAlpha = 0.6;
      ctx.setLineDash([2, 3]);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(lane.x0, Math.round(hv.y) + 0.5);
      ctx.lineTo(lane.x0 + lane.width, Math.round(hv.y) + 0.5);
      ctx.moveTo(Math.round(hv.x) + 0.5, VALUE_BAND_PX);
      ctx.lineTo(Math.round(hv.x) + 0.5, h);
      ctx.stroke();
      ctx.restore();
      if (state.drag === null) {
        const right = hv.x < lane.x0 + lane.width - 100;
        drawChip(ctx, hv.text, right ? hv.x + 8 : hv.x - 8, hv.y + 12, theme, right ? 'left' : 'right');
      }
    }
  }
  if (state.drag !== null) {
    const d = state.drag;
    const right = d.x < width - 110;
    drawChip(ctx, d.text, right ? d.x + 12 : d.x - 12, d.y - 14, theme, right ? 'left' : 'right', theme.accent);
  }
  if (state.warning !== null) {
    const w = state.warning;
    const right = w.x < width - 160;
    drawChip(ctx, w.text, right ? w.x + 12 : w.x - 12, w.y + 14, theme, right ? 'left' : 'right', theme.danger);
  }
}
